import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { mockBenefits, mockWhatsAppGroups } from '../data/mock';
import {
  Calendar,
  GraduationCap,
  Scale,
  MessageCircle,
  Users,
  Network,
  FileText,
  FileCheck,
  DollarSign,
  Calculator,
  Home,
  ClipboardCheck,
  Building,
  Award,
  Tag,
  CreditCard,
  Library,
  Heart,
  Search,
  ExternalLink,
  ArrowRight,
  Star,
  CheckCircle2,
  Sparkles,
  Cpu
} from 'lucide-react';

const iconMap = {
  Calendar,
  GraduationCap,
  Scale,
  MessageCircle,
  Users,
  Network,
  FileText,
  FileCheck,
  DollarSign,
  Calculator,
  Home,
  ClipboardCheck,
  Building,
  Award,
  Tag,
  CreditCard,
  Library,
  Heart,
  Star
};

const categories = [
  { id: 'all', name: 'Todos', icon: Star },
  { id: 'eventos', name: 'Eventos', icon: Calendar },
  { id: 'formacion', name: 'Formación', icon: GraduationCap },
  { id: 'asesoria', name: 'Asesorías', icon: Scale },
  { id: 'networking', name: 'Networking', icon: Network },
  { id: 'recursos', name: 'Recursos', icon: FileText },
  { id: 'servicios', name: 'Servicios', icon: Building },
  { id: 'descuentos', name: 'Descuentos', icon: Tag },
  { id: 'comunidad', name: 'Comunidad', icon: Users },
];

const BeneficiosPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const filteredBenefits = mockBenefits.filter(benefit => {
    const matchesSearch = benefit.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         benefit.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || benefit.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
              Beneficios Exclusivos
            </p>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Todo lo que obtienes como afiliado UPIM
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Master Classes quincenales, asesoría legal, acceso a convenios exclusivos, 
              precios preferenciales y mucho más.
            </p>
            
            {/* QR Afiliacion */}
            <div className="inline-flex items-center bg-white rounded-xl shadow-md p-4 border border-gray-100">
              <img
                src="https://i0.wp.com/upim.org/wp-content/uploads/2023/11/QR-Afiliacion-UPIM.png?resize=80%2C80&ssl=1"
                alt="QR Afiliación UPIM"
                className="h-20 w-20 mr-4"
              />
              <div className="text-left">
                <p className="font-semibold text-gray-900">Escanea para afiliarte</p>
                <p className="text-sm text-gray-500">Accede a todos estos beneficios</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Avalúos con IA - Destacado */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Cpu className="h-5 w-5 text-gray-400" />
                <span className="text-gray-400 uppercase tracking-wider text-sm font-medium">
                  Tecnología Exclusiva
                </span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Avalúos en segundos con Inteligencia Artificial
              </h2>
              <p className="text-gray-400 text-lg mb-6 leading-relaxed">
                Genera avalúos en segundos con información fidedigna de más de 10 millones 
                de propiedades en toda la República basada en avalúos certificados.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-500" />
                  <span className="text-gray-300">Más de 10 millones de propiedades analizadas</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-500" />
                  <span className="text-gray-300">Resultados en segundos</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-500" />
                  <span className="text-gray-300">Cobertura nacional</span>
                </li>
              </ul>
              <Button 
                size="lg" 
                className="bg-white text-gray-900 hover:bg-gray-100"
                asChild
              >
                <a href="https://portalinmobiliario.api4it.com/v1/brainbow_upim" target="_blank" rel="noopener noreferrer">
                  Acceder a Avalúos con IA
                  <ExternalLink className="ml-2 h-5 w-5" />
                </a>
              </Button>
            </div>
            <div className="relative hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&h=450&fit=crop"
                alt="Avalúos con IA"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 bg-white border-b sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            <div className="relative w-full lg:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar beneficios..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
              {categories.map((cat) => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={selectedCategory === cat.id ? 'bg-gray-900 hover:bg-gray-800' : 'border-gray-300'}
                >
                  <cat.icon className="h-4 w-4 mr-1" />
                  {cat.name}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBenefits.map((benefit) => {
              const IconComponent = iconMap[benefit.icon] || Star;
              return (
                <Card 
                  key={benefit.id} 
                  className="bg-white border border-gray-100 hover:shadow-lg transition-all duration-300 group"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center group-hover:bg-gray-900 transition-colors">
                        <IconComponent className="h-6 w-6 text-gray-700 group-hover:text-white transition-colors" />
                      </div>
                      <Badge variant="outline" className="text-xs border-gray-300 text-gray-600">
                        {categories.find(c => c.id === benefit.category)?.name || 'General'}
                      </Badge>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">{benefit.description}</p>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-gray-700 hover:text-gray-900 hover:bg-gray-100 p-0"
                      asChild
                    >
                      <Link to="/afiliacion">
                        Acceder al beneficio
                        <ArrowRight className="ml-1 h-4 w-4" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {filteredBenefits.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No se encontraron beneficios con esos criterios.</p>
            </div>
          )}
        </div>
      </section>

      {/* WhatsApp Groups */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
              Networking
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Grupos de WhatsApp por zona
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Únete a los grupos exclusivos para afiliados UPIM y conecta con 
              profesionales de tu zona.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mockWhatsAppGroups.map((group) => (
              <a
                key={group.id}
                href={group.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group border border-gray-100"
              >
                <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center mr-4 group-hover:bg-gray-900 transition-colors">
                  <MessageCircle className="h-6 w-6 text-gray-600 group-hover:text-white transition-colors" />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 group-hover:text-gray-700 transition-colors">
                    {group.name}
                  </h4>
                  <p className="text-sm text-gray-500">{group.members} miembros</p>
                </div>
                <ExternalLink className="h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Women's Committee */}
      <section className="py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
                Comunidad
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Comité Inmobiliario de Mujeres
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Únete a esta iniciativa de comunidad para fortalecer el apoyo a mujeres 
                profesionales y participar en actividades para mujeres en situación vulnerable.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Networking exclusivo para mujeres</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Eventos y capacitaciones especializadas</span>
                </li>
                <li className="flex items-center space-x-3">
                  <CheckCircle2 className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-600">Apoyo a causas sociales</span>
                </li>
              </ul>
              <Button 
                className="bg-gray-900 hover:bg-gray-800"
                asChild
              >
                <a href="https://chat.whatsapp.com/HFILZyjA87VHa4DhOhAmIq" target="_blank" rel="noopener noreferrer">
                  Únete al Comité
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600&h=400&fit=crop"
                alt="Profesionales mujeres"
                className="rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            ¿Listo para acceder a todos estos beneficios?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Afíliate ahora y comienza a disfrutar de todos los beneficios exclusivos 
            que UPIM tiene para ti.
          </p>
          <Button 
            size="lg" 
            className="bg-white text-gray-900 hover:bg-gray-100 px-8 h-12"
            asChild
          >
            <Link to="/afiliacion">
              Afíliate por $3,500/año
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default BeneficiosPage;
